// Identity (P-1a, DM-0): minted on the device on first visit, kept on the device. uuid4 + a random
// 256-bit secret in base64url. The server stores only SHA-256 of the secret. Never logged.
import type { IdentityProof } from "./types";

export interface KeyValue {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const KEY = "majlis.identity";

function base64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function mintIdentity(rng: Crypto = crypto): IdentityProof {
  const secret = new Uint8Array(32);
  rng.getRandomValues(secret);
  return { id: rng.randomUUID(), secret: base64url(secret) };
}

export class IdentityStore {
  private cached: IdentityProof | null = null;
  constructor(
    private readonly kv: KeyValue | null,
    private readonly rng: Crypto = crypto,
  ) {}

  get(): IdentityProof {
    if (this.cached) return this.cached;
    try {
      const raw = this.kv?.getItem(KEY);
      if (raw) {
        const v = JSON.parse(raw) as IdentityProof;
        if (typeof v.id === "string" && typeof v.secret === "string") return (this.cached = v);
      }
    } catch {
      // unreadable storage: mint below (a private window keeps it for the page's life)
    }
    return this.renew();
  }

  /** MAJLIS-67: the identity this device already holds, WITHOUT minting one - a first visit has none, so it asks the
   * server for nothing and its start screen is today's, unchanged. */
  peek(): IdentityProof | null {
    if (this.cached) return this.cached;
    try {
      const raw = this.kv?.getItem(KEY);
      if (raw) {
        const v = JSON.parse(raw) as IdentityProof;
        if (typeof v.id === "string" && typeof v.secret === "string") return (this.cached = v);
      }
    } catch {
      // unreadable storage: nothing held
    }
    return null;
  }

  /** identity_mismatch: mint a new identity (the old one's seats stay with the old secret). */
  renew(): IdentityProof {
    this.cached = mintIdentity(this.rng);
    try {
      this.kv?.setItem(KEY, JSON.stringify(this.cached));
    } catch {
      // storage refused: the identity lives for this page only
    }
    return this.cached;
  }
}
